import { Fragment } from "react"
import type { FC, ReactElement } from "react"

import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import eq from "lodash-es/eq"
import initial from "lodash-es/initial"
import isEmpty from "lodash-es/isEmpty"
import last from "lodash-es/last"
import map from "lodash-es/map"
import reject from "lodash-es/reject"

import { CheckMarkIcon } from "@/3514/assets"
import { Divider, EDividerColor } from "@/3514/components"
import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"
import { THEME } from "@/3514/theme"

import { EParticipantOnboardingStep as EStep } from "./participant.onboarding.api.config"
import styles from "./participant.onboarding.styles"
import uiHooks, { type TParticipantOnboardingStepsState } from "./participant.onboarding.ui.hooks"
import { type TParticipantOnboardingStep as TStep } from "./participant.onboarding.ui.types"

const { useSteps } = uiHooks
const { stepper: classes } = styles

const ParticipantOnboardingStepper: FC = (): ReactElement => {
    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const isMotionReduced: boolean = useReducedMotion()

    const { currentStepByProgress: currentStep, allSteps }: TParticipantOnboardingStepsState = useSteps()

    const stepsToRender: TStep[] = isEmpty(allSteps)
        ? []
        : eq(last<TStep>(allSteps).name, EStep.Final)
          ? reject<TStep>(initial<TStep>(allSteps), { isVisibleInStepper: false })
          : reject<TStep>(allSteps, { isVisibleInStepper: false })

    return (
        <AnimatePresence mode="wait">
            {isEmpty(stepsToRender) ? null : (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: isMotionReduced ? 0 : 0.2, type: "tween", ease: "easeInOut" }}
                    className={classes.container()}
                >
                    {map<TStep, ReactElement>(
                        stepsToRender,
                        (s: TStep, idx: number, { length }: TStep[]): ReactElement => {
                            const isLast: boolean = idx === length - 1
                            const isCurrent: boolean = eq(s.name, currentStep?.name)
                            const { isCompleted }: TStep = s

                            return (
                                <Fragment key={`phs-${s.name}`}>
                                    <div className={classes.dot({ isLast, isCurrent, isCompleted })}>
                                        {isLast ? (
                                            <CheckMarkIcon fillColor={THEME.COLORS.blue[200]} />
                                        ) : (
                                            <i className={classes.dotMark({ isLast, isCurrent, isCompleted })} />
                                        )}
                                    </div>

                                    {!isLast && (
                                        <Divider
                                            width={breakpoints.xs || breakpoints.sm ? "-webkit-fill-available" : "40px"}
                                            variant={EDividerColor.SemiDarkBlue}
                                            height={0.5}
                                            margins={{ top: "auto", bottom: "auto" }}
                                        />
                                    )}
                                </Fragment>
                            )
                        }
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    )
}

ParticipantOnboardingStepper.displayName = "ParticipantOnboardingStepper"

export { ParticipantOnboardingStepper }
